import { LoadingScreen } from "~/features/addToCart/components/AddToCartProcess/Modals/LoadingScreen";
import { ErrorLoadingScreen } from "~/features/addToCart/components/AddToCartProcess/Modals/ErrorLoadingScreen";
import {
  ContractAvailableScreen,
  ContractButtons,
  ContractLoadErrorScreen,
} from "~/features/addToCart/components/AddToCartProcess/Modals/ContractAvailableScreen";
import { AddToCartScreen } from "~/features/addToCart/components/AddToCartProcess/Modals/AddToCartScreen";

import { KITSummaryScreen } from "~/features/addToCart/components/AddToCartProcess/Modals/KITSummaryScreen";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { AtcScreen } from "~/features/addToCart/flow/types";
import { useTranslation } from "react-i18next";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { AtcDialogShell } from "./shell/AtcDialogShell";
import { useAddToCartFlowContext } from "./AddToCartFlowContext";
import { Alert, Stack } from "~/common/mui";
import { AtcContentLayout } from "./shell/AtcContentLayout";
import { AtcProductDetails } from "./shell/AtcProductDetails";
import type { ReactNode } from "react";
import { AtcCloseButton, AtcPrimaryAction } from "./shell/AtcShellActions";
import { canSeeAddToCartButton } from "~/utils/features";
import { useAddToCartDerived } from "../../hooks/AddToCartDerivedContext";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";

export const AtcWizardScreens = ({
  canSeeAddToCartDisclaimer,
  canSubmitToCartOrQuote,
  flowKind,
  isSending,
  productDescriptor,
  screen,
  submit,
}: {
  canSeeAddToCartDisclaimer: boolean;
  canSubmitToCartOrQuote: boolean;
  flowKind: "addToCart" | "editPart";
  isSending: boolean;
  productDescriptor: ProductDescriptor;
  screen: AtcScreen;
  submit: () => void;
}) => {
  const { t } = useTranslation();

  let mainContent: ReactNode;

  let footer;
  switch (screen) {
    case "loading":
      mainContent = <LoadingScreen />;
      footer = <DefaultButtons productDescriptor={productDescriptor} />;
      break;
    case "errorLoading":
      mainContent = <ErrorLoadingScreen />;
      footer = <DefaultButtons productDescriptor={productDescriptor} />;
      break;
    case "contractAvailable":
      if (flowKind === "addToCart") {
        mainContent = <ContractAvailableScreen />;
        footer = <ContractButtons productDescriptor={productDescriptor} />;
      }
      break;
    case "contractLoadErrored":
      if (flowKind === "addToCart") {
        mainContent = <ContractLoadErrorScreen />;
        footer = <ContractButtons productDescriptor={productDescriptor} disabled />;
      }
      break;
    case "kitSummary":
      mainContent = (
        <KITSummaryScreen
          canSeeAddToCartDisclaimer={canSeeAddToCartDisclaimer}
          canSubmitToCartOrQuote={canSubmitToCartOrQuote}
          productDescriptor={productDescriptor}
        />
      );
      footer = <DefaultButtons productDescriptor={productDescriptor} onAdd={submit} />;
      break;
    case "defaultForm":
      mainContent = (
        <AddToCartScreen
          canSeeAddToCartDisclaimer={canSeeAddToCartDisclaimer}
          productDescriptor={productDescriptor}
          sending={isSending}
        />
      );
      footer = <DefaultButtons productDescriptor={productDescriptor} onAdd={submit} sending={isSending} />;
      break;
    case "errorSending":
      mainContent = (
        <Stack gap={2}>
          <Alert severity={"error"}>{t("addToCartPopup.add.error")}</Alert>
          <AddToCartScreen
            canSeeAddToCartDisclaimer={canSeeAddToCartDisclaimer}
            productDescriptor={productDescriptor}
            sending={isSending}
          />
        </Stack>
      );
      footer = <DefaultButtons productDescriptor={productDescriptor} />;
      break;
  }

  const { requestClose } = useAddToCartFlowContext();
  const handleClose = () => requestClose();
  const { main } = useAddToCartDerived();
  const packCode = main.selectedPack?.packCode ?? INVALID_PACKAGE_CODE;

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <AtcDialogShell title={t("addToCartPopup.title")} onClose={handleClose} footer={footer}>
        <AtcContentLayout
          info={<AtcProductDetails descriptor={productDescriptor} packCode={packCode} />}
          main={mainContent}
        />
      </AtcDialogShell>
    </ProductDescriptorContext>
  );
};

const DefaultButtons = ({
  productDescriptor,
  onAdd,
  disabled,
  sending,
}: {
  productDescriptor: ProductDescriptor;
  onAdd?: () => void;
  disabled?: boolean;
  sending?: boolean;
}) => {
  const { requestClose } = useAddToCartFlowContext();
  const handleClose = () => requestClose();

  const showAddToCart = canSeeAddToCartButton();

  return (
    <Stack direction={"row"} gap={1}>
      <AtcCloseButton onClick={handleClose} />
      {showAddToCart && (
        <AtcPrimaryAction
          onClick={onAdd}
          disabled={disabled || !onAdd}
          fullWidth
          descriptor={productDescriptor}
          loading={sending}
        />
      )}
    </Stack>
  );
};
